import { useState, FC, ReactNode } from 'react';
import { ToTop } from '../modules';

type SectionProps = {
  title: string;
  children: ReactNode;
};

const Section: FC<SectionProps> = ({ title, children }) => (
  <section style={{ marginBottom: '2.5rem' }}>
    <h2
      className="privacypolicy_contents_h2"
      style={{
        borderBottom: '2px solid #eee',
        paddingBottom: '0.5rem',
        marginBottom: '1.5rem',
      }}
    >
      {title}
    </h2>
    <div style={{ lineHeight: 1.8, color: '#555' }}>{children}</div>
  </section>
);

type PolicyHeaderProps = {
  title: string;
  subTitle: string;
  lastUpdate: string;
};

const PolicyHeader: FC<PolicyHeaderProps> = ({
  title,
  subTitle,
  lastUpdate,
}) => (
  <header style={{ textAlign: 'center', marginBottom: '4rem' }}>
    <h1 className="privacypolicy_title" style={{ marginBottom: '0.5rem' }}>
      {title}
    </h1>
    <h2
      className="privacypolicy_subTitle"
      style={{ marginBottom: '1rem', color: '#666' }}
    >
      {subTitle}
    </h2>
    <p
      className="privacypolicy_contents_body"
      style={{ color: '#888', fontSize: '0.9rem' }}
    >
      {lastUpdate}
    </p>
  </header>
);

type LangButtonProps = {
  onClick: () => void;
  isActive: boolean;
  children: ReactNode;
  style?: React.CSSProperties;
};

const LangButton: FC<LangButtonProps> = ({
  onClick,
  isActive,
  children,
  style,
}) => (
  <button
    onClick={onClick}
    style={{
      ...style,
      padding: '8px 16px',
      cursor: 'pointer',
      border: isActive ? '1px solid #333' : '1px solid #ccc',
      backgroundColor: isActive ? '#333' : 'white',
      color: isActive ? 'white' : '#333',
      borderRadius: '4px',
      transition: 'all 0.3s',
      minWidth: '110px',
      whiteSpace: 'nowrap',
      height: '40px',
    }}
  >
    {children}
  </button>
);

const TukanomaPrivacypolicy: FC = () => {
  const [lang, setLang] = useState<'jp' | 'en'>('jp');

  // 日本語コンテンツ
  const jpContent = {
    title: 'Tukanoma Camera',
    subTitle: 'プライバシーポリシー',
    lastUpdate: '最終更新日: 2025年11月29日',
    introduction:
      '本プライバシーポリシーは、Takumi Kanaya（以下「当方」）が作成したモバイルデバイス向けアプリケーション「Tukanoma Camera」（以下「本アプリ」）に適用されます。本サービスは、Freemiumサービスとして提供され、「現状有姿」で使用されることを意図しています。',
    section1Title: '1. 情報の収集と利用',
    section1Body:
      '本アプリは、お客様がダウンロードして使用する際に情報を収集します。この情報には以下のようなものが含まれる場合があります。',
    section1_1Title: '1.1. 取得する情報',
    section1_1Li1: 'デバイスのインターネットプロトコルアドレス（例：IPアドレス）',
    section1_1Li2: '本アプリの訪問ページ、訪問日時、それらのページでの滞在時間',
    section1_1Li3: '本アプリの利用時間',
    section1_1Li4: 'モバイルデバイスで使用されているオペレーティングシステム',
    section1_2Title: '1.2. 位置情報',
    section1_2Body:
      '本アプリは、お客様のデバイスの位置情報を収集します。これは、当方がお客様のおおよその地理的位置を特定し、以下の方法で利用するのに役立ちます。',
    section1_2Li1: '位置情報サービス：当方は、位置データを利用して、パーソナライズされたコンテンツ、関連する推奨事項、位置情報ベースのサービスなどの機能を提供します。',
    section1_2Li2: '分析と改善：集計され匿名化された位置データは、ユーザーの行動分析、トレンドの特定、および本アプリの全体的なパフォーマンスと機能の改善に役立ちます。',
    section1_2Li3: '第三者サービス：当方は、匿名化された位置データを外部サービスに定期的に送信する場合があります。これらのサービスは、本アプリの強化と提供内容の最適化を支援します。',

    section2Title: '2. 第三者へのアクセス',
    section2Body:
      '集計され匿名化されたデータのみが、本アプリおよびサービスの改善を支援するために外部サービスに定期的に送信されます。当方は、本プライバシーステートメントに記載されている方法でのみ、お客様の情報を第三者と共有します。',
    section2_1Title: '2.1. 情報の開示',
    section2_1Body: '当方は、以下の場合にユーザー提供情報および自動的に収集された情報を開示することがあります。',
    section2_1Li1: '召喚状や同様の法的手続きに従うなど、法律で義務付けられている場合',
    section2_1Li2: '当方の権利、お客様の安全または他者の安全を守るため、詐欺を調査するため、または政府の要請に応じるために開示が必要であると誠実に信じる場合',
    section2_1Li3: '当方に代わって業務を行う信頼できるサービス提供者と共有する場合。これらの提供者は、当方が開示した情報を独立して使用することはなく、本プライバシーステートメントに定められた規則を遵守することに同意しています。',

    section3Title: '3. オプトアウトの権利',
    section3Body:
      '本アプリをアンインストールすることで、すべての情報収集を簡単に停止することができます。お使いのモバイルデバイス、またはモバイルアプリケーションマーケットプレイスやネットワークの一部として利用可能な、標準的なアンインストールプロセスを使用することができます。',
    section4Title: '4. データ保持方針',
    section4Body:
      '当方は、お客様が本アプリを使用している限り、ユーザー提供データを保持し、その後も合理的な期間保持します。お客様が提供したユーザー提供データの削除を希望される場合は、brownpowder@gmail.com までご連絡ください。合理的な時間内に対応いたします。',
    section5Title: '5. お子様の利用について',
    section5Body:
      '当方は、13歳未満の子供から意図的にデータを収集したり、マーケティングを行ったりすることはありません。当方は、子供から意図的に個人を特定できる情報を収集することはありません。当方は、すべての子供に対し、本アプリおよび/またはサービスを通じて個人を特定できる情報を決して送信しないよう推奨します。保護者および法的後見人の方は、お子様のインターネット利用状況を監視し、本ポリシーを徹底させるため、許可なくアプリケーションやサービスを通じて個人情報を提供しないようお子様に指導することをお勧めします。お子様が個人情報を提供したと思われる場合は、当方が必要な措置を講じられるよう、ご連絡ください（budoudesign.test@gmail.com）。また、お客様の国において個人情報の処理に同意するには、16歳以上である必要があります（一部の国では、保護者または後見人が代わって同意することを認めている場合があります）。',
    section6Title: '6. セキュリティ',
    section6Body:
      '当方は、お客様の情報の機密性を保護することに関心を持っています。当方が処理・維持する情報を保護するために、物理的、電子的、手続き的な保護措置を提供します。',
    section7Title: '7. 変更',
    section7Body:
      '本プライバシーポリシーは、いかなる理由でも随時更新されることがあります。当方は、このページを新しいプライバシーポリシーで更新することによって、プライバシーポリシーの変更をお客様に通知します。継続的な使用はすべての変更を承認したものとみなされるため、変更については定期的に本プライバシーポリシーを参照することをお勧めします。',
    section8Title: '8. お客様の同意',
    section8Body:
      '本アプリを使用することにより、お客様は、本プライバシーポリシーに定められたとおり、現在および当方によって修正されたお客様の情報の処理に同意するものとします。',

    section9Title: '9. お問い合わせ',
    section9Body:
      '本アプリの利用中にプライバシーに関するご質問がある場合、または当方の慣行についてご質問がある場合は、電子メールにて budoudesign.test@gmail.com までご連絡ください。',
  };

  // 英語コンテンツ
  const enContent = {
    title: 'Tukanoma Camera',
    subTitle: 'Privacy Policy',
    lastUpdate: 'Effective as of November 29, 2025',
    introduction:
      'This privacy policy applies to the Tukanoma Camera app (hereby referred to as "Application") for mobile devices that was created by Takumi Kanaya (hereby referred to as "Service Provider") as a Freemium service. This service is intended for use "AS IS".',
    section1Title: '1. Information Collection and Use',
    section1Body:
      'The Application collects information when you download and use it. This information may include information such as:',
    section1_1Title: '1.1. Collected Information',
    section1_1Li1: "Your device's Internet Protocol address (e.g. IP address)",
    section1_1Li2: 'The pages of the Application that you visit, the time and date of your visit, the time spent on those pages',
    section1_1Li3: 'The time spent on the Application',
    section1_1Li4: 'The operating system you use on your mobile device',
    section1_2Title: '1.2. Location Information',
    section1_2Body:
      "The Application collects your device's location, which helps the Service Provider determine your approximate geographical location and make use of in below ways:",
    section1_2Li1: 'Geolocation Services: The Service Provider utilizes location data to provide features such as personalized content, relevant recommendations, and location-based services.',
    section1_2Li2: 'Analytics and Improvements: Aggregated and anonymized location data helps the Service Provider to analyze user behavior, identify trends, and improve the overall performance and functionality of the Application.',
    section1_2Li3: 'Third-Party Services: Periodically, the Service Provider may transmit anonymized location data to external services. These services assist them in enhancing the Application and optimizing their offerings.',

    section2Title: '2. Third Party Access',
    section2Body:
      'Only aggregated, anonymized data is periodically transmitted to external services to aid the Service Provider in improving the Application and their service. The Service Provider may share your information with third parties in the ways that are described in this privacy statement.',
    section2_1Title: '2.1. Disclosure of Information',
    section2_1Body: 'The Service Provider may disclose User Provided and Automatically Collected Information:',
    section2_1Li1: 'as required by law, such as to comply with a subpoena, or similar legal process;',
    section2_1Li2: 'when they believe in good faith that disclosure is necessary to protect their rights, protect your safety or the safety of others, investigate fraud, or respond to a government request;',
    section2_1Li3: 'with their trusted services providers who work on their behalf, do not have an independent use of the information we disclose to them, and have agreed to adhere to the rules set forth in this privacy statement.',

    section3Title: '3. Opt-Out Rights',
    section3Body:
      'You can stop all collection of information by the Application easily by uninstalling it. You may use the standard uninstall processes as may be available as part of your mobile device or via the mobile application marketplace or network.',
    section4Title: '4. Data Retention Policy',
    section4Body:
      "The Service Provider will retain User Provided data for as long as you use the Application and for a reasonable time thereafter. If you'd like them to delete User Provided Data that you have provided via the Application, please contact them at budoudesign.test@gmail.com and they will respond in a reasonable time.",
    section5Title: '5. Children',
    section5Body:
      "The Service Provider does not use the Application to knowingly solicit data from or market to children under the age of 13. The Service Provider does not knowingly collect personally identifiable information from children. The Service Provider encourages all children to never submit any personally identifiable information through the Application and/or Services. The Service Provider encourage parents and legal guardians to monitor their children's Internet usage and to help enforce this Policy by instructing their children never to provide personally identifiable information through the Application and/or Services without their permission. If you have reason to believe that a child has provided personally identifiable information to the Service Provider through the Application and/or Services, please contact the Service Provider (budoudesign.test@gmail.com) so that they will be able to take the necessary actions. You must also be at least 16 years of age to consent to the processing of your personally identifiable information in your country (in some countries we may allow your parent or guardian to do so on your behalf).",
    section6Title: '6. Security',
    section6Body:
      'The Service Provider is concerned about safeguarding the confidentiality of your information. The Service Provider provides physical, electronic, and procedural safeguards to protect information the Service Provider processes and maintains.',
    section7Title: '7. Changes',
    section7Body:
      'This Privacy Policy may be updated from time to time for any reason. The Service Provider will notify you of any changes to the Privacy Policy by updating this page with the new Privacy Policy. You are advised to consult this Privacy Policy regularly for any changes, as continued use is deemed approval of all changes.',
    section8Title: '8. Your Consent',
    section8Body:
      'By using the Application, you are consenting to the processing of your information as set forth in this Privacy Policy now and as amended by us.',
    section9Title: '9. Contact Us',
    section9Body:
      'If you have any questions regarding privacy while using the Application, or have questions about the practices, please contact the Service Provider via email at budoudesign.test@gmail.com.',
  };

  const currentContent = lang === 'jp' ? jpContent : enContent;

  return (
    <div className="privacypolicy" style={{ width: '100%', minHeight: '100vh', backgroundColor: '#fff', padding: '4rem 2rem' }}>
      <ToTop />
      <div
        className="privacypolicy_contents"
        style={{
          maxWidth: '800px',
          margin: '0 auto',
          fontFamily: 'sans-serif',
        }}
      >
        <div style={{ textAlign: 'right', marginBottom: '2rem' }}>
          <LangButton
            onClick={() => setLang('jp')}
            isActive={lang === 'jp'}
            style={{ marginRight: '10px' }}
          >
            日本語
          </LangButton>
          <LangButton onClick={() => setLang('en')} isActive={lang === 'en'}>
            English
          </LangButton>
        </div>

        <PolicyHeader
          title={currentContent.title}
          subTitle={currentContent.subTitle}
          lastUpdate={currentContent.lastUpdate}
        />

        <p
          className="privacypolicy_contents_body"
          style={{ marginBottom: '2.5rem' }}
        >
          {currentContent.introduction}
        </p>

        <Section title={currentContent.section1Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section1Body}
          </p>
          <h3 className="privacypolicy_contents_h3">
            {currentContent.section1_1Title}
          </h3>
          <ul className="privacypolicy_contents_ul">
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section1_1Li1}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section1_1Li2}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section1_1Li3}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section1_1Li4}
            </li>
          </ul>
          <h3 className="privacypolicy_contents_h3">
            {currentContent.section1_2Title}
          </h3>
          <p className="privacypolicy_contents_body">
            {currentContent.section1_2Body}
          </p>
          <ul className="privacypolicy_contents_ul">
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section1_2Li1}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section1_2Li2}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section1_2Li3}
            </li>
          </ul>

        </Section>

        <Section title={currentContent.section2Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section2Body}
          </p>
          <h3 className="privacypolicy_contents_h3">
            {currentContent.section2_1Title}
          </h3>
          <p className="privacypolicy_contents_body">
            {currentContent.section2_1Body}
          </p>
          <ul className="privacypolicy_contents_ul">
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section2_1Li1}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section2_1Li2}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section2_1Li3}
            </li>
          </ul>

        </Section>

        <Section title={currentContent.section3Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section3Body}
          </p>
        </Section>

        <Section title={currentContent.section4Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section4Body}
          </p>
        </Section>

        <Section title={currentContent.section5Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section5Body}
          </p>
        </Section>

        <Section title={currentContent.section6Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section6Body}
          </p>
        </Section>

        <Section title={currentContent.section7Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section7Body}
          </p>
        </Section>

        <Section title={currentContent.section8Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section8Body}
          </p>
        </Section>

        <Section title={currentContent.section9Title}>
          {currentContent.section9Body}
        </Section>
      </div>
    </div>
  );
};

export default TukanomaPrivacypolicy;
